import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {useEffect, useState} from 'react'
import {createSubscriptionPlan, getSubscriptionPlans} from '../SuperAdminUser_Api'
import Swal from 'sweetalert2'
import {CircularProgress} from '@mui/material'

interface subscriptionPlansDataType {
  allowed_admin_count: number
  allowed_subscription_free_property_count: number
  allowed_user_count: number
  is_subscription_free: number
  subscription_id: number
  subscription_level: number
  subscription_name: string
  subscription_status: number
  subscription_uuid: string
}
interface changeSubscriptionModalDataType {
  showSubscriptionModal: any
  setShowSubscriptionModal: (showSubscriptionModal: string | null) => void
  accountUUID: string
  getUser: () => void
}
const modalsRoot = document.getElementById('root-modals') || document.body

const ChangeSubscriptionModal = ({
  showSubscriptionModal,
  setShowSubscriptionModal,
  accountUUID,
  getUser,
}: changeSubscriptionModalDataType) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [subscriptionPlans, setSubscriptionPlans] = useState<subscriptionPlansDataType[]>()
  const [createPlanBtnLoading, setCreatePlanBtnLoading] = useState<number | null>(null)

  // function to get all the subscription plan
  const getAllSubscriptionPlans = async () => {
    setLoading(true)
    try {
      const {data: response} = await getSubscriptionPlans()
      if (response.code === '200') {
        setSubscriptionPlans(response.data)
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
          text: response.msg_details,
          allowOutsideClick: true,
        })
      }
    } catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Error',
        text: error,
        allowOutsideClick: true,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getAllSubscriptionPlans()
  }, [])

  // function to close the modal
  const handleClose = () => {
    setShowSubscriptionModal(null)
  }

  // function to choose the subscription plan
  const choosePlan = async (subscription_id: number) => {
    if (accountUUID) {
      setCreatePlanBtnLoading(subscription_id)
      try {
        const {data: response} = await createSubscriptionPlan(accountUUID, subscription_id)
        if (response.code === '200') {
          setShowSubscriptionModal(null)
          getUser()
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
            text: response.msg_details,
            allowOutsideClick: true,
          })
        } else {
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
            text: response.msg_details,
            allowOutsideClick: true,
          })
        }
      } catch (error: any) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: error,
          allowOutsideClick: true,
        })
      } finally {
        setCreatePlanBtnLoading(null)
      }
    }
  }
  return createPortal(
    <Modal
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered min-vw-100'
      show={showSubscriptionModal}
      onHide={handleClose}
      backdrop={true}
    >
      {/* Begin: Modal header */}
      <div className='modal-header pt-5 pb-5'>
        {/* Begin: Modal Heading */}
        <h2>Subscription Plans</h2>
        {/* End: Modal Heading */}
        {/* Begin::Close button */}
        <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
          <i className='bi bi-x-lg' onClick={handleClose}></i>
        </div>
        {/* end::Close button */}
      </div>
      {/* End: Modal header */}

      {loading ? (
        // loader start
        <div
          className='modal-body p-10 d-flex justify-content-center align-items-center vh-70'
          style={{height: '50vh'}}
        >
          <CircularProgress />
        </div>
      ) : (
        <div className='modal-body p-5'>
          {/*Begin:: Form Content */}
          <div className='d-flex gap-10 align-items-center justify-content-center flex-wrap px-5'>
            {subscriptionPlans &&
              subscriptionPlans.map((subscriptionDetails, index) => {
                return (
                  <div key={index}>
                    <div className={`card shadow card-xl-stretch`}>
                      {/* begin::Body */}
                      <div className='card-body p-0'>
                        {/* begin::Header */}
                        <div
                          className={`px-9 pt-7 card-rounded h-275px w-100 bg-primary`}
                          style={{marginTop: '-10px'}}
                        >
                          {/* begin::Heading */}
                          <div className='d-flex flex-stack mt-2 justify-content-center'>
                            <h3 className='m-0 text-white fw-bold fs-1x'>
                              {subscriptionDetails.subscription_name}
                            </h3>
                          </div>
                          {/* end::Heading */}
                          {/* begin::Balance */}
                          <div className='d-flex text-center flex-column text-white pt-8'>
                            <span className='fw-semibold fs-7'>Plan Price</span>
                            <span className='fw-bold fs-2x pt-1'>₹0.00</span>
                          </div>
                          {/* end::Balance */}
                        </div>
                        {/* end::Header */}
                        {/* begin::Items */}
                        <div
                          className='shadow-xs card-rounded mx-9 mb-7 px-6 py-9 position-relative z-index-1 bg-body'
                          style={{marginTop: '-100px'}}
                        >
                          {/* begin::Item */}
                          <div className='d-flex align-items-center mb-6'>
                            {/* begin::Symbol */}
                            <div className='symbol symbol-45px w-40px me-5'>
                              <span className='symbol-label bg-lighten'>
                                <i className='bi bi-house-door svg-icon-1 fs-1 text-muted'></i>
                              </span>
                            </div>
                            {/* end::Symbol */}
                            {/* begin::Description */}
                            <div className='d-flex align-items-center flex-wrap w-100'>
                              {/* begin::Title */}
                              <div className='mb-1 pe-3 flex-grow-1'>
                                <span className='fs-5 text-gray-800 text-hover-primary fw-bold'>
                                  Properties
                                </span>
                                <div className='text-gray-400 fw-semibold fs-7'>{`${subscriptionDetails.allowed_subscription_free_property_count} Properties`}</div>
                              </div>
                              {/* end::Title */}
                            </div>
                            {/* end::Description */}
                          </div>
                          {/* end::Item */}
                          {/* begin::Item */}
                          <div className='d-flex align-items-center mb-6'>
                            {/* begin::Symbol */}
                            <div className='symbol symbol-45px w-40px me-5'>
                              <span className='symbol-label bg-lighten'>
                                <i
                                  className='bi bi-people-fill svg-icon-1 fs-1 text-muted'
                                  title='Property Users'
                                ></i>
                              </span>
                            </div>
                            {/* end::Symbol */}
                            {/* begin::Description */}
                            <div className='d-flex align-items-center flex-wrap w-100'>
                              {/* begin::Title */}
                              <div className='mb-1 pe-3 flex-grow-1'>
                                <span className='fs-5 text-gray-800 text-hover-primary fw-bold'>
                                  Users
                                </span>
                                <div className='text-gray-400 fw-semibold fs-7'>{`${subscriptionDetails.allowed_user_count} Users`}</div>
                              </div>
                              {/* end::Title */}
                            </div>
                            {/* end::Description */}
                          </div>
                          {/* end::Item */}
                          {/* begin::Item */}
                          <div className='d-flex align-items-center'>
                            {/* begin::Symbol */}
                            <div className='symbol symbol-45px w-40px me-5'>
                              <span className='symbol-label bg-lighten'>
                                <i className='bi bi-person-fill svg-icon-1 fs-1 text-muted'></i>
                              </span>
                            </div>
                            {/* end::Symbol */}
                            {/* begin::Description */}
                            <div className='d-flex align-items-center flex-wrap w-100'>
                              {/* begin::Title */}
                              <div className='mb-1 pe-3 flex-grow-1'>
                                <span className='fs-5 text-gray-800 text-hover-primary fw-bold'>
                                  Account
                                </span>
                                <div className='text-gray-400 fw-semibold fs-7'>{`${subscriptionDetails.allowed_admin_count} Admin accounts`}</div>
                              </div>
                              {/* end::Title */}
                            </div>
                            {/* end::Description */}
                          </div>
                          {/* end::Item */}
                        </div>
                        {/* end::Items */}
                      </div>
                      {/* Begin: choose button if plan is not selected */}
                      {/* {!selectedPlanUUID && ( */}
                      <div className='d-flex justify-content-center pb-5'>
                        <button
                          className='btn btn-sm fw-bold btn-primary fs-7'
                          onClick={() => choosePlan(subscriptionDetails.subscription_id)}
                          disabled={Boolean(createPlanBtnLoading)}
                        >
                          {createPlanBtnLoading === subscriptionDetails.subscription_id ? (
                            <span className='indicator-progress' style={{display: 'block'}}>
                              Processing...
                              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                          ) : (
                            'Choose'
                          )}
                        </button>
                      </div>
                      {/* )} */}
                      {/* End: choose button if plan is not selected */}
                      {/* end::Body */}
                    </div>
                  </div>
                )
              })}
          </div>
          {/*end:: Form Content */}
        </div>
      )}
    </Modal>,
    modalsRoot
  )
}

export default ChangeSubscriptionModal
